/** External app URLs — CoolClimate ecosystem tools (membership may be required). */
export const ECOSYSTEM_APPS = [
  {
    id: 'climateplans',
    name: 'ClimatePlans',
    description: 'Climate action planning',
    href: '/',
    external: false,
  },
  {
    id: 'calculator',
    name: 'CoolClimate Household Calculator',
    description: 'Community engagement',
    href: process.env.REACT_APP_CALCULATOR_URL || 'https://coolclimate.org/calculator',
    external: true,
  },
  {
    id: 'business',
    name: 'CoolClimate Calculator for Businesses',
    description: 'Organizational emissions',
    href: process.env.REACT_APP_BUSINESS_URL || 'https://coolclimate.org/org-calc',
    external: true,
  },
  {
    id: 'challenge',
    name: 'Cool Campus Challenge',
    description: 'Campus engagement and behavior change',
    href: process.env.REACT_APP_CHALLENGE_URL || 'https://coolcampuschallenge.org/',
    external: true,
  },
] as const;

export const ECOSYSTEM_SECTION = {
  title: 'One Platform. Multiple Climate Solutions.',
  subtitleLead:
    'CoolClimate Household Calculator and CoolClimate Calculator for Businesses.',
} as const;

/** Membership application intake (Phase 1). */
export const MEMBERSHIP_SIGNUP_URL = '/membership/apply';

/** TODO: Replace with Calendly, HubSpot, or team scheduling link. */
export const MEMBERSHIP_DEMO_URL = '/demo';

export const HERO_HEADLINE = 'California Climate Action Portal';

export const HERO_SUBHEADLINE =
  'GHG inventories, key progress indicators, policy analysis, community engagement tools, reporting, and support for California local governments.';

export const WORKFLOW_SECTION = {
  title: 'Filling the Gaps to Advance Climate Action',
  subtitle: 'A complete workflow for local governments — from understanding conditions through implementation.',
} as const;

export const WORKFLOW_COLUMNS = [
  {
    title: 'Understand',
    items: ['GHG inventories', 'Equity indicators', 'Peer benchmarking', 'Community profiles'],
  },
  {
    title: 'Prioritize',
    items: ['Scenario modeling', 'Policy evaluation', 'Peer comparisons', 'Climate action planning'],
  },
  {
    title: 'Implement',
    items: ['Reports', 'Community engagement', 'Progress tracking', 'Funding support'],
  },
] as const;

export const MEMBERSHIP_COMPARE_SECTION = {
  title: 'Public Access and CoolClimate Network Membership',
  subtitle:
    'Explore every California community for free. Membership adds professional tools, reporting, and support for staff who lead climate action.',
} as const;

export const PUBLIC_ACCESS_ITEMS = [
  'Explore all California communities',
  'View indicators and inventories',
  'Compare peer jurisdictions',
  'Access public dashboards',
] as const;

export const AFFILIATE_MEMBERSHIP = {
  title: 'Affiliate Membership',
  priceLabel: 'Starting at $2,500/year',
  items: [
    'Data and figure downloads',
    'Downloadable GHG inventory reports',
    'ICLEI-compatible GHG inventory reporting',
    'Statewide peer benchmarking',
    'Member webinars and working groups',
    'Quarterly climate intelligence newsletter',
    'Recognition as a CoolClimate member',
    'Early access to new datasets and indicators',
    'Site license for CoolClimate household and business calculators',
  ],
} as const;

export const REGULAR_MEMBERSHIP = {
  title: 'Regular Membership',
  priceLabel: 'Starting at $5,000/year',
  includesNote: 'Includes Affiliate benefits, plus:',
  items: [
    'Tract-level consumption-based GHG inventory',
    'Policy planning tools with GHG mitigation potential',
    'Tailored climate action plan template',
    'Custom dashboards',
    'Local content management',
    'Role-based user accounts',
    'Technical support',
    'Staff training',
    'Challenge software and technical support',
    'Advisory board participation',
    'Priority feature requests',
  ],
} as const;

export const FEATURED_TOOLS_SECTION = {
  title: 'Featured Members Only Tools',
  subtitle: 'Capabilities included with CoolClimate Network membership for local government teams.',
} as const;

export const FEATURED_MEMBER_TOOLS = [
  {
    id: 'data-download',
    title: 'Data & Figure Download',
    description: 'Export charts, tables, and datasets for reports and presentations.',
    href: '#public-vs-membership',
  },
  {
    id: 'custom-portal',
    title: 'Custom Climate Action Portal',
    description: 'A fully customized portal tailored to your jurisdiction\'s goals and workflows.',
    href: '#public-vs-membership',
  },
  {
    id: 'advanced-analysis',
    title: 'Advanced Analysis',
    description: 'Identify high-impact opportunities.',
    href: '/law-impact',
  },
  {
    id: 'policy-explorer',
    title: 'Policy Explorer',
    description: 'See policies implemented by peer communities.',
    href: '/law-impact',
  },
  {
    id: 'scenario-planning',
    title: 'Scenario Planning',
    description: 'Estimate impacts of future actions.',
    href: '#public-vs-membership',
  },
  {
    id: 'local-government-workspace',
    title: 'Local Government Workspace',
    description: 'Customize local dashboards and reports.',
    href: '#public-vs-membership',
  },
] as const;

export const SUCCESS_STORIES_SECTION = {
  title: 'Success Stories',
  subtitle: 'How California communities use CoolClimate tools for planning and implementation.',
} as const;

export const SUCCESS_STORIES = [
  {
    id: 'coolcalifornia',
    title: 'CoolCalifornia',
    excerpt:
      '20+ years working in partnership with the California Air Resources Board to develop tools for California residents, businesses, schools, and communities.',
    isComingSoon: false,
  },
  {
    id: 'san-francisco',
    title: 'City and County of San Francisco',
    excerpt:
      'Developed industry-leading consumption-based GHG inventory methodology and is implementing a city government employee engagement competition.',
    isComingSoon: false,
  },
  {
    id: 'more-stories',
    title: 'More member stories coming soon',
    excerpt:
      'Future case studies will highlight how cities, counties, campuses, and organizations use CoolClimate tools for planning and implementation.',
    isComingSoon: true,
  },
] as const;

export const WHY_COOLCLIMATE_SECTION = {
  title: 'Why UC Berkeley\u2019s CoolClimate Network?',
  items: [
    '20+ years developing leading climate research and software for California',
    'Partner on CoolCalifornia.org',
    'Winner of a $1.3M grant funded by the California Legislature to develop ClimatePlans.org',
    'Technical advisory board with input from dozens of experts',
    'Developer of the CoolClimate Calculator with millions of users',
    '10+ years running behavior-based competitions to engage California residents, universities, and businesses',
  ],
} as const;

export const FINAL_CTA = {
  title: 'Join California\u2019s Climate Planning Network',
  text: 'Cities, counties, campuses, and organizations use CoolClimate tools to support planning, reporting, engagement, and implementation.',
} as const;
